import { CheckIcon, PencilIcon } from '@heroicons/react/24/outline';
import { DmButton } from '../DmControlPrimitives';
import { notificationLabel, type NotificationEntry } from './excludedTeethTaxonomy';
import { ToothGrid } from './ToothGrid';
import { ToothMiniChart } from './ToothMiniChart';
import { formatTeeth } from './toothNumbering';

interface ExcludedTeethNotificationRowProps {
    entry: NotificationEntry;
    teeth: readonly string[];
    /** Shows "Category › Observation" under the label, in search and the overall view. */
    showPath: boolean;
    isOpen: boolean;
    isEdited: boolean;
    /** Set right after "apply to every notification" ran from this row. */
    appliedCount: number | null;
    onToggle: () => void;
    onChange: (teeth: string[]) => void;
    onApplyToObservation: () => void;
}

export function ExcludedTeethNotificationRow({
    entry,
    teeth,
    showPath,
    isOpen,
    isEdited,
    appliedCount,
    onToggle,
    onChange,
    onApplyToObservation
}: ExcludedTeethNotificationRowProps) {
    return (
        <li>
            <div className="flex items-center gap-4 py-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <span className="truncate text-[13px] font-medium text-gray-800">
                            {notificationLabel(entry.reference)}
                        </span>
                        {isEdited && (
                            <span className="shrink-0 whitespace-nowrap rounded-full bg-[#E6F2F3] px-2 py-0.5 text-xs font-medium text-[#007E87]">
                                Edited
                            </span>
                        )}
                    </div>
                    {showPath && (
                        <p className="truncate text-xs text-gray-500">
                            {entry.category.label} › {entry.observation.label}
                        </p>
                    )}
                </div>
                <ToothMiniChart teeth={teeth} />
                <span
                    className={`w-36 shrink-0 truncate text-right text-[13px] tabular-nums ${teeth.length ? 'text-gray-700' : 'text-gray-400'}`}
                    title={teeth.length ? formatTeeth(teeth) : undefined}
                >
                    {teeth.length ? formatTeeth(teeth) : 'No teeth selected'}
                </span>
                <DmButton
                    size="sm"
                    kind={isOpen ? 'primary' : 'secondary'}
                    icon={isOpen ? CheckIcon : PencilIcon}
                    onClick={onToggle}
                    aria-expanded={isOpen}
                    className="w-[84px] shrink-0"
                >
                    {isOpen ? 'Done' : 'Edit'}
                </DmButton>
            </div>
            {isOpen && (
                <div className="pb-4 pt-1">
                    <ToothGrid selected={teeth} onChange={onChange} />
                    <div className="mt-3 flex items-center gap-3 pl-[68px]">
                        <DmButton kind="link" onClick={onApplyToObservation}>
                            Apply these teeth to every notification in {entry.observation.label}
                        </DmButton>
                        {appliedCount !== null && (
                            <span className="text-xs text-gray-500">
                                Applied to {appliedCount} notifications
                            </span>
                        )}
                    </div>
                </div>
            )}
        </li>
    );
}
